/**
 * Grammar Practice API Service
 * 
 * Handles all API calls related to grammar practice
 */

import {
  Exercise,
  ExerciseRequest,
  GrammarTopic,
  ListTopicsParams,
  UserExerciseAttempt,
  ExerciseDiscussion,
  DiscussionComment,
  CreateCommentRequest,
  UpdateCommentRequest,
} from '../types/grammarPractice';

const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

/**
 * Get authentication headers with JWT token
 */
function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
}

/**
 * Get a random exercise based on filters
 */
export async function getExercise(request: ExerciseRequest): Promise<Exercise> {
  const response = await fetch(`${BASE_URL}/grammar_practice/get_exercise/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch exercise');
  }

  return response.json();
}

/**
 * List available grammar topics with optional filters
 */
export async function listTopics(params: ListTopicsParams): Promise<GrammarTopic[]> {
  const queryParams = new URLSearchParams();
  queryParams.append('language', params.language);
  
  if (params.proficiency_level) {
    queryParams.append('proficiency_level', params.proficiency_level);
  }

  const response = await fetch(
    `${BASE_URL}/grammar_practice/list_topics/?${queryParams.toString()}`,
    {
      method: 'GET',
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch topics');
  }

  return response.json();
}

/**
 * Get user's exercise attempt history
 */
export async function getMyAttempts(): Promise<UserExerciseAttempt[]> {
  const response = await fetch(`${BASE_URL}/grammar_practice/my_attempts/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch attempts');
  }

  return response.json();
}

/**
 * Client-side answer checking (instant feedback without API call)
 * Safely handles undefined/null values
 */
export function checkAnswerLocally(userAnswer: string | undefined, correctAnswer: string | undefined): boolean {
  // Defensive checks for undefined/null values
  if (!userAnswer || !correctAnswer) {
    console.error('checkAnswerLocally: Missing answer', { userAnswer, correctAnswer });
    return false;
  }

  const normalize = (str: string) => str.trim().toLowerCase();

  try {
    return normalize(userAnswer) === normalize(correctAnswer);
  } catch (error) {
    console.error('checkAnswerLocally: Error comparing answers', error);
    return false;
  }
}

// Discussion API functions

/**
 * Get discussion for an exercise
 */
export async function getExerciseDiscussion(exerciseId: number): Promise<ExerciseDiscussion> {
  const response = await fetch(`${BASE_URL}/grammar_practice/discussions/${exerciseId}/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch discussion');
  }

  return response.json();
}

/**
 * Add a comment to an exercise discussion
 */
export async function addComment(
  exerciseId: number,
  request: CreateCommentRequest
): Promise<DiscussionComment> {
  const response = await fetch(`${BASE_URL}/grammar_practice/discussions/${exerciseId}/comments/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to add comment');
  }

  return response.json();
}

/**
 * Update a comment
 */
export async function updateComment(
  commentId: number,
  request: UpdateCommentRequest
): Promise<DiscussionComment> {
  const response = await fetch(`${BASE_URL}/grammar_practice/discussions/comments/${commentId}/`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update comment');
  }

  return response.json();
}

/**
 * Delete a comment
 */
export async function deleteComment(commentId: number): Promise<void> {
  const response = await fetch(`${BASE_URL}/grammar_practice/discussions/comments/${commentId}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete comment');
  }
}
